import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, RotateCcw, X, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const TYPE_LABELS = {
  trial_reminder: '48hr Trial Reminder',
  trial_followup: 'Post-Trial Follow-up',
  auto_followup: 'Auto Follow-up',
  transactional: 'System / Booking Email',
};

export const FailedEmailsPanel = () => {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/admin/failed-emails');
      setItems(res.data.failed_emails || []);
    } catch (e) {
      console.error('Failed to load failed emails', e);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      const res = await api.post(`/admin/failed-emails/${id}/${action}`);
      toast.success(res.data.message || 'Done');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const pending = items.filter(i => i.status === 'needs_review');
  if (items.length === 0) return null;

  return (
    <Card className="bg-[#111] border-red-500/30" data-testid="failed-emails-panel">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          Failed Emails — Needs Review ({pending.length})
        </CardTitle>
        <p className="text-xs text-gray-400">
          These emails could not be sent after 5 attempts. Fix the cause (usually sender domain verification), then press Resend — nothing is lost.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map(item => (
          <div key={item.id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-black/40 border border-[#222]" data-testid={`failed-email-row-${item.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white text-sm font-medium">{item.student_name || item.recipient || 'Unknown'}</span>
                <Badge variant="outline" className="text-[10px] text-gray-300">{TYPE_LABELS[item.email_type] || item.email_type}</Badge>
                {item.status === 'requeued' && (
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px]">Re-queued</Badge>
                )}
                {item.status === 'resent' && (
                  <Badge className="bg-green-500/20 text-green-300 border-green-500/30 text-[10px]">Resent</Badge>
                )}
                {item.status === 'archived' && (
                  <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 text-[10px]">Archived — will not send</Badge>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{item.recipient} · {item.attempts} attempts</p>
              <p className="text-xs text-red-300/90 mt-0.5 break-all">{item.error}</p>
            </div>
            {item.status === 'needs_review' && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10 h-8"
                  disabled={busyId === item.id}
                  onClick={() => act(item.id, 'retry')}
                  data-testid={`retry-failed-email-${item.id}`}>
                  {busyId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                  Resend
                </Button>
                <Button size="sm" variant="outline" className="border-gray-600 text-gray-400 hover:bg-white/5 h-8"
                  disabled={busyId === item.id}
                  onClick={() => act(item.id, 'dismiss')}
                  data-testid={`dismiss-failed-email-${item.id}`}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default FailedEmailsPanel;
